import {tx, ptx, usePageText} from '../lib/page-text';
import { useAuth } from '../lib/auth-store';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { CreateStreamFromPreset } from '../components/CreateStreamFromPreset';

/**
 * Blueprints — the kinds of live stream this tenant can run.
 *
 * Eight ship with the platform, one per archetype. Everything else a business
 * needs is one of those shapes plus a policy change: a deposition is a
 * consultation kept for longer, a Sunday service is a webinar with donations
 * instead of a Q&A queue. So the job of this page is cloning and editing, not
 * building from nothing.
 */

interface Blueprint {
  id: string;
  slug: string;
  name: string;
  archetype: string;
  description: string | null;
  version: number;
  system: boolean;
  config: any;
}

const LATENCY_LABEL: Record<string, string> = {
  REALTIME: 'sub-300ms',
  LOW_LATENCY: '1–3s',
  BROADCAST: '5–15s',
};

const supportedAccess = (b: Blueprint) => ['public', 'invite'].includes(b.config.access?.mode ?? 'public');

export function BlueprintsPage() {
  usePageText();
  const canEdit = useAuth(s => ['OWNER', 'ADMIN', 'MEMBER'].includes(s.user?.role ?? ''));
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Blueprint | null>(null);
  const [cloning, setCloning] = useState<Blueprint | null>(null);
  const [creating, setCreating] = useState<Blueprint | null>(null);

  const list = useQuery({
    queryKey: ['blueprints'],
    queryFn: async () => (await api.get('/me/blueprints')).data as Blueprint[],
  });

  const archive = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/me/blueprints/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['blueprints'] }),
  });

  const items = list.data ?? [];
  const system = items.filter((b) => b.system);
  const mine = items.filter((b) => !b.system);

  return (
    <div>
      <h1>{tx("Stream templates")}</h1>
      <p className="subtitle">{tx("Reusable settings for your streams: features, latency, audience access and recording retention. Copy a starter, customize it, then create streams from your saved template.")}</p>

      {creating && <CreateStreamFromPreset title={creating.name} blueprint={creating} onClose={() => setCreating(null)} />}
      {editing && <BlueprintEditor blueprint={editing} onClose={() => setEditing(null)} />}
      {cloning && <BlueprintCloner from={cloning} onClose={() => setCloning(null)} />}

      {list.isPending && <p role="status">{tx("Loading templates…")}</p>}
      {list.isError && <p role="alert">{ptx("Could not load templates. {v0}", {v0: (<button className="btn outline" onClick={() => void list.refetch()}>{tx("Retry")}</button>)})}</p>}
      {archive.isError && <p role="alert">{messageOf(archive.error)}</p>}
      <h3 style={{ marginTop: 24 }}>{tx("Your saved templates")}</h3>
      {mine.length === 0 ? (
        <div className="card">
          <div className="muted" style={{ fontSize: 13 }}>{tx("No saved templates yet. Copy a starter below and adjust its settings.")}</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table>
            <thead><tr><th>{tx("Name")}</th><th>{tx("Shape")}</th><th>{tx("Latency")}</th><th>{tx("Access")}</th><th>{tx("Keeps")}</th><th></th></tr></thead>
            <tbody>
              {mine.map((b) => (
                <tr key={b.id}>
                  <td><strong>{b.name}</strong><div className="muted" style={{ fontSize: 11 }}>{b.slug} · v{b.version}</div></td>
                  <td><span className="badge">{b.archetype}</span></td>
                  <td className="muted">{LATENCY_LABEL[b.config.latency] ? tx(LATENCY_LABEL[b.config.latency]) : b.config.latency}</td>
                  <td className="muted">{b.config.access?.mode}{!supportedAccess(b) && <div role="note">{tx("Needs access update")}</div>}</td>
                  <td className="muted">{b.config.retention?.days ? `${b.config.retention.days}d` : tx("forever")}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn sm" disabled={!canEdit || !supportedAccess(b)} onClick={() => setCreating(b)}>{tx("Create stream")}</button>{' '}
                    <button className="btn outline sm" disabled={!canEdit} onClick={() => setEditing(b)}>{tx("Edit")}</button>{' '}
                    <button className="btn ghost sm" disabled={!canEdit || archive.isPending} onClick={() => {
                      if (confirm(`Retire "${b.name}"? Streams already created from it keep working.`)) archive.mutate(b.id);
                    }}>{tx("Retire")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 style={{ marginTop: 28 }}>{tx("Starter templates")}</h3>
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))' }}>
        {system.map((b) => (
          <div key={b.id} className="card">
            <div className="row-between" style={{ alignItems: 'baseline' }}>
              <strong>{b.name}</strong>
              <span className="badge">{b.archetype}</span>
            </div>
            <p className="muted" style={{ fontSize: 12, margin: '6px 0 10px' }}>{b.description}</p>
            <div className="flex gap" style={{ flexWrap: 'wrap', fontSize: 11, marginBottom: 10 }}>
              <span className="badge">{LATENCY_LABEL[b.config.latency] ? tx(LATENCY_LABEL[b.config.latency]) : b.config.latency}</span>
              <span className="badge">{b.config.capture?.orientation}</span>
              <span className="badge">{b.config.capture?.publishers === 1 ? tx("1 camera") : ptx("{v0} publishers", {v0: (b.config.capture?.publishers)})}</span>
              <span className="badge">{b.config.access?.mode}</span>
              <span className="badge">{b.config.retention?.days ? ptx("keep {v0}d", {v0: (b.config.retention.days)}) : tx("keep forever")}</span>
            </div>
            {!supportedAccess(b) && <p className="muted">{tx("This starter needs an access update before it can be copied.")}</p>}
            <button className="btn outline sm" disabled={!canEdit || !supportedAccess(b)} onClick={() => setCloning(b)}>{tx("Clone & customise")}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Cloning asks only for what must differ; everything else comes from the source. */
function BlueprintCloner({ from, onClose }: { from: Blueprint; onClose: () => void }) {
  usePageText();
  const qc = useQueryClient();
  const [name, setName] = useState(`${from.name} (ours)`);
  const [slug, setSlug] = useState(`${from.slug}-ours`);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => (await api.post('/me/blueprints', {
      name, slug, archetype: from.archetype, description: from.description ?? undefined, fromId: from.id,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['blueprints'] }); onClose(); },
    onError: (e: any) => setErr(messageOf(e)),
  });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginTop: 0 }}>{ptx("Clone “{v0}”", {v0: (from.name)})}</h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>{tx("Starts as an exact copy. Edit it afterwards to change latency, modules, access or retention.")}</p>
      <div className="flex gap" style={{ flexWrap: 'wrap' }}>
        <input className="input" style={{ maxWidth: 240 }} value={name} onChange={(e) => setName(e.target.value)} placeholder={tx("name")} />
        <input className="input" style={{ maxWidth: 200 }} value={slug} onChange={(e) => setSlug(e.target.value)} placeholder={tx("slug")} />
        <button className="btn" disabled={create.isPending || !name.trim()} onClick={() => { setErr(null); create.mutate(); }}>
          {create.isPending ? tx("Creating…") : tx("Create")}
        </button>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
      </div>
      {err && <div style={{ color: 'var(--danger, #f31260)', fontSize: 13, marginTop: 8 }}>{err}</div>}
    </div>
  );
}

/** The fields worth changing, as controls rather than raw JSON. */
function BlueprintEditor({ blueprint, onClose }: { blueprint: Blueprint; onClose: () => void }) {
  usePageText();
  const qc = useQueryClient();
  const [cfg, setCfg] = useState(() => JSON.parse(JSON.stringify(blueprint.config)));
  const [name, setName] = useState(blueprint.name);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/me/blueprints/${blueprint.id}`, { name, config: cfg })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['blueprints'] }); onClose(); },
    onError: (e: any) => setErr(messageOf(e)),
  });

  const set = (path: string[], value: unknown) => {
    setCfg((prev: any) => {
      const next = JSON.parse(JSON.stringify(prev));
      let node = next;
      for (const k of path.slice(0, -1)) node = (node[k] ??= {});
      node[path[path.length - 1]] = value;
      return next;
    });
  };

  const FEATURES = ['chat', 'reactions', 'polls', 'qa', 'handRaise', 'spotlight', 'whiteboard', 'recording', 'transcription', 'moderation'];

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="row-between">
        <h3 style={{ marginTop: 0 }}>{ptx("Edit “{v0}”", {v0: (blueprint.name)})}</h3>
        <span className="badge">v{blueprint.version}</span>
      </div>

      <label className="label">{tx("Name")}</label>
      <input className="input" style={{ maxWidth: 320 }} value={name} onChange={(e) => setName(e.target.value)} />

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', marginTop: 16 }}>
        <div>
          <label className="label">{tx("Latency")}</label>
          <select className="input" value={cfg.latency} onChange={(e) => set(['latency'], e.target.value)}>
            <option value="REALTIME">{tx("Real-time — sub-300ms")}</option>
            <option value="LOW_LATENCY">{tx("Low latency — 1–3s")}</option>
            <option value="BROADCAST">{tx("Broadcast — 5–15s")}</option>
          </select>
        </div>
        <div>
          <label className="label">{tx("Orientation")}</label>
          <select className="input" value={cfg.capture?.orientation} onChange={(e) => set(['capture', 'orientation'], e.target.value)}>
            <option value="16:9">{tx("16:9 landscape")}</option>
            <option value="9:16">{tx("9:16 vertical")}</option>
          </select>
        </div>
        <div>
          <label className="label">{tx("Publishers")}</label>
          <input className="input" type="number" min={1} max={100} value={cfg.capture?.publishers ?? 1}
                 onChange={(e) => set(['capture', 'publishers'], Number(e.target.value))} />
        </div>
        <div>
          <label className="label" htmlFor="template-access">{tx("Who may watch")}</label>
          <select id="template-access" className="input" value={cfg.access?.mode} onChange={(e) => set(['access', 'mode'], e.target.value)}>
            <option value="public">{tx("Anyone with the link")}</option>
            <option value="invite">{tx("Invite only")}</option>
            {!['public', 'invite'].includes(cfg.access?.mode) && <option value={cfg.access?.mode} disabled>{ptx("{v0} — unavailable", {v0: (cfg.access?.mode)})}</option>}
          </select>
        </div>
        <div>
          <label className="label">{tx("Viewer layout")}</label>
          <select className="input" value={cfg.viewer?.layout} onChange={(e) => set(['viewer', 'layout'], e.target.value)}>
            <option value="theatre">{tx("Theatre")}</option>
            <option value="vertical">{tx("Vertical")}</option>
            <option value="grid">{tx("Grid")}</option>
          </select>
        </div>
        <div>
          <label className="label">{tx("Keep recordings for (days)")}</label>
          <input className="input" type="number" min={1} max={3650} value={cfg.retention?.days ?? ''}
                 placeholder={tx("blank = forever")}
                 onChange={(e) => set(['retention', 'days'], e.target.value === '' ? null : Number(e.target.value))} />
        </div>
      </div>

      <p className="muted">{tx("Ticketed, KYC and SSO viewing access are not available. Invite only uses a signed viewer invitation; registration alone does not grant access.")}</p>
      <label className="label" style={{ marginTop: 16 }}>{tx("Modules")}</label>
      <div className="flex gap" style={{ flexWrap: 'wrap' }}>
        {FEATURES.map((f) => (
          <label key={f} className="flex gap" style={{ alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={!!cfg.features?.[f]} onChange={(e) => set(['features', f], e.target.checked)} />
            {f}
          </label>
        ))}
      </div>

      {cfg.access?.mode !== 'public' && cfg.access?.mode !== 'invite' && (
        <div style={{ fontSize: 12, color: 'var(--warning, #f5a524)', marginTop: 12 }}>
          {cfg.access.mode}{tx(" access is unavailable. Choose Public or Invite only before using this template. Existing streams keep their original access; this does not make them public.")}</div>
      )}

      {err && <div style={{ color: 'var(--danger, #f31260)', fontSize: 13, marginTop: 10 }}>{err}</div>}

      <div className="flex gap" style={{ marginTop: 16 }}>
        <button className="btn" disabled={save.isPending || !['public', 'invite'].includes(cfg.access?.mode)} onClick={() => { setErr(null); save.mutate(); }}>
          {save.isPending ? tx("Saving…") : tx("Save changes")}
        </button>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
      </div>
    </div>
  );
}

/** The API answers validation failures with a list; show all of them. */
function messageOf(e: any): string {
  const m = e?.response?.data?.message;
  if (Array.isArray(m)) return m.join(' · ');
  return typeof m === 'string' ? m : 'Something went wrong';
}
